import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AppMark, LightLogo } from "./AppLogo";
import { CalendarDays, LayoutDashboard, Users, Sparkles, MapPin, UserCog, FileSignature, Wallet, BarChart3,
  Settings, LogOut, Menu, X, ExternalLink, CreditCard, Building2, ShoppingBag, Newspaper, MessageCircle, Megaphone, ClipboardList, Car, HandCoins, Gift, FileText, Clock } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useT, LangSelect } from "../lib/i18n";
import { NativeSelect } from "./Common";
import { api, loginPath } from "../lib/api";
import { AccountSwitch, SupportBanner, MyAccount } from "./AccountSwitch";

const NAV = [
  { to: "/app", key: "dashboard", icon: LayoutDashboard, perm: "agenda.read", end: true },
  { to: "/app/agenda", key: "agenda", icon: CalendarDays, perm: "agenda.read" },
  { to: "/app/availability", key: "availability", icon: Clock, perm: "agenda.read", pro: true },
  { to: "/app/clients", key: "clients", icon: Users, perm: "clients.read" },
  { to: "/app/inbox", key: "inbox", label: "Inbox", icon: MessageCircle, perm: "messages.send" },
  { to: "/app/home-visits", key: "home_visits", label: "Home visits", icon: Car, perm: "agenda.read", feature: "home_visits" },
  { to: "/app/earnings", key: "earnings", label: "Earnings", icon: HandCoins, perm: "agenda.read" },
  { to: "/app/packages", key: "packages", label: "Packages & gifts", icon: Gift, perm: "agenda.read", feature: "packages" },
  { to: "/app/revenue", key: "revenue", icon: Wallet, perm: "payments.read" },
  { to: "/app/invoices", key: "invoices", label: "Invoices", icon: FileText, perm: "payments.read", feature: "invoices" },
  { to: "/app/marketing", key: "marketing", label: "Marketing", icon: Megaphone, perm: "settings.write", feature: "marketing" },
  { to: "/app/forms", key: "forms", label: "Forms & notes", icon: ClipboardList, perm: "clinical.write" },
  { to: "/app/shop", key: "shop", label: "Shop", icon: ShoppingBag, perm: "shop.read", feature: "shop" },
  { to: "/app/content", key: "content", label: "Reviews & Blog", icon: Newspaper, perm: "content.write", feature: "content" },
  { to: "/app/reports", key: "reports", icon: BarChart3, perm: "reports.read" },
  { to: "/app/consents", key: "consents", icon: FileSignature, perm: "consents.write" },
  { to: "/app/services", key: "services", icon: Sparkles, perm: "services.write" },
  { to: "/app/locations", key: "locations", icon: MapPin, perm: "locations.write" },
  { to: "/app/staff", key: "staff", icon: UserCog, perm: "staff.write" },
  { to: "/app/settings", key: "settings", icon: Settings, perm: "settings.write" },
  { to: "/app/billing", key: "billing", label: "Billing", icon: CreditCard, perm: "billing" },
  { to: "/app/platform", key: "platform", label: "Platform admin", icon: Building2, platform: true },
];

function Brand() {
  const { settings } = useApp();
  const b = settings.branding || {};
  const dark = (b.sidebar_style || "dark") !== "light";
  return (
    <div className="flex items-center gap-3" data-testid="brand">
      {!b.logo ? <AppMark className="h-10" />
        : dark ? <LightLogo src={b.logo} testId="brand-logo" className="h-10 max-w-[160px]" />
          : <img src={b.logo} alt="logo" data-testid="brand-logo" className="h-10 w-auto max-w-[160px] object-contain" />}
    </div>
  );
}

function useUnread() {
  const { can } = useApp();
  const [n, setN] = useState(0);
  const allowed = can("messages.send");
  useEffect(() => {
    if (!allowed) return undefined;
    const load = () => api.get("/inbox/unread").then((r) => setN(r.data.total)).catch(() => {});
    load();
    const h = setInterval(load, 20000);
    return () => clearInterval(h);
  }, [allowed]);
  return n;
}

function NavItems({ onClick, horizontal }) {
  const { can, user } = useApp();
  const { t } = useT();
  const unread = useUnread();
  return NAV.filter((n) => (n.platform ? !!user.platform_role && !user.support_session : !user.clinic?.is_hq && can(n.perm) && (!n.pro || ["owner", "professional"].includes(user.role) || can("staff.write")) && user.clinic?.features?.[n.feature] !== false)).map((n) => (
    <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick} data-testid={`nav-${n.key}`}
      className={({ isActive }) => `sidebar-link flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${isActive ? "active" : ""} ${horizontal ? "whitespace-nowrap" : ""}`}>
      <n.icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{n.label || t(n.key)}</span>
      {n.key === "inbox" && unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold grid place-items-center" data-testid="nav-inbox-badge">{unread > 99 ? "99+" : unread}</span>}
    </NavLink>
  ));
}

function Controls({ dark }) {
  const { user, logout, locations, locationId, setLocationId, settings } = useApp();
  const { t } = useT();
  const hq = user.clinic?.is_hq;
  return (
    <div className="flex flex-wrap lg:flex-nowrap items-center gap-2">
      {!hq && <NativeSelect testId="global-location-select" value={locationId} onChange={setLocationId}
        className={dark ? "bg-transparent text-inherit border-white/20" : ""}
        options={[{ value: "", label: t("all_locations") }, ...locations.map((l) => ({ value: l.id, label: `${l.name} · ${l.currency}` }))]} />}
      <LangSelect testId="lang-toggle" />
      <AccountSwitch dark={dark} />
      <MyAccount dark={dark} />
      {!hq && <a href={`/book/${settings.slug || user.clinic.slug}`} target="_blank" rel="noreferrer" data-testid="open-booking-page" title={t("public_booking")}
        className="h-10 shrink-0 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-black/5">
        <ExternalLink className="h-3.5 w-3.5" /><span className="hidden 2xl:inline">{t("public_booking")}</span>
      </a>}
      <div className="text-sm font-semibold leading-tight px-2 max-w-[160px] truncate" data-testid="current-user">{user.name}</div>
      <button data-testid="logout-btn" onClick={logout} className="h-10 w-10 shrink-0 grid place-items-center rounded-md hover:bg-black/10" title={t("logout")}>
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

function TrialBanner() {
  const { user } = useApp();
  const c = user.clinic;
  if (c.status === "active" || c.plan === "internal" || c.plan === "free") return null;
  const days = c.trial_ends_at ? Math.max(Math.ceil((new Date(c.trial_ends_at) - new Date()) / 86400000), 0) : 0;
  const text = c.status === "trial" ? `Free trial: ${days} day${days === 1 ? "" : "s"} left` : c.status === "past_due" ? "Payment failed: please update your card" : "Subscription inactive";
  return (
    <div data-testid="trial-banner" className="relative z-20 flex flex-wrap items-center justify-center gap-3 bg-[hsl(var(--brand-accent))] px-4 py-2 text-sm font-medium text-black/80">
      {text}
      {user.role === "owner" && <Link to="/app/billing" className="underline" data-testid="trial-banner-billing-link">Subscribe</Link>}
    </div>
  );
}

export default function AppShell() {
  const { user, settings } = useApp();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to={loginPath()} replace />;
  if (user.clinic.is_hq && !loc.pathname.startsWith("/app/platform")) return <Navigate to="/app/platform" replace />;
  if (!user.clinic.is_active && !loc.pathname.startsWith("/app/billing") && !loc.pathname.startsWith("/app/platform")) return <Navigate to="/app/billing" replace />;
  const topbar = (settings.branding || {}).layout === "topbar";

  if (topbar) {
    return (
      <div className="min-h-screen grain">
        <SupportBanner />
        <TrialBanner />
        <header className="sidebar sticky top-0 z-30 border-b">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-8 py-3">
            <Brand />
            <Controls dark />
          </div>
          <nav className="flex gap-1 overflow-x-auto px-4 sm:px-8 pb-3"><NavItems horizontal /></nav>
        </header>
        <main className="relative z-10 p-4 sm:p-6 lg:p-10"><Outlet /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain lg:flex">
      <aside className={`sidebar fixed inset-y-0 left-0 z-40 w-64 flex flex-col p-4 overflow-y-auto overscroll-contain transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex shrink-0 items-center justify-between mb-8 px-1 pt-1">
          <Brand />
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex flex-col gap-1"><NavItems onClick={() => setOpen(false)} /></nav>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 lg:ml-64 min-w-0">
        <SupportBanner />
        <TrialBanner />
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-background/80 backdrop-blur-xl px-4 sm:px-8 py-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar"><Menu className="h-5 w-5" /></button>
          <div className="ml-auto"><Controls /></div>
        </header>
        <main className="relative z-10 p-4 sm:p-6 lg:p-10"><Outlet /></main>
      </div>
    </div>
  );
}
